import {ActionReducer, createFeature, createReducer, createSelector, on, select} from '@ngrx/store';
import {EntityState, EntityAdapter, createEntityAdapter, Dictionary} from '@ngrx/entity';
import {Update} from "@ngrx/entity";
import {uniq, uniqBy} from "lodash";
import {OrderModel} from "../../models/order.model";
import {OrdersActions, OrdersAPiActions, OrdersChannelActions} from "./orders.action";

export interface OrdersState extends EntityState<OrderModel> {
  cursor: string;
  isLoading: boolean;
  initialized: boolean;
  infiniteScrollOrderIds: string[]
}

const adapter: EntityAdapter<OrderModel> = createEntityAdapter<OrderModel>();

const initialState: OrdersState = adapter.getInitialState({
  cursor: null,
  isLoading: false,
  initialized: false,
  infiniteScrollOrderIds: []
});


const reducer: ActionReducer<OrdersState> = createReducer(
  initialState,

  on(OrdersChannelActions.notificationReceived, (state, {notification}) => {
    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      return adapter.setOne(notification, state);
    } else {
      return state;
    }
  }),

  on(OrdersChannelActions.partialNotificationReceived, (state, {notification}) => {

    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      const changes: Update<OrderModel> = {
        id: notification.id,
        changes: {...notification}
      }
      return adapter.updateOne(changes, state);
    } else {
      return state;
    }
  }),

  on(OrdersAPiActions.getOrder, (state) => ({...state, isLoading: true})),

  on(OrdersAPiActions.getOrderSuccess, (state, {order}) => {
    return adapter.setOne(order, {...state, isLoading: false, initialized: true})
  }),

  on(OrdersAPiActions.getInfiniteScrollOrders, (state) => ({...state, isLoading: true})),

  on(OrdersAPiActions.getInfiniteScrollOrdersSuccess, (state, {orders, cursor}) => {
    return adapter.setMany(orders,
      {
        ...state,
        infiniteScrollOrderIds: uniq([...state.infiniteScrollOrderIds, ...orders.map(r => r.id)]),
        cursor: cursor,
        isLoading: false,
        initialized: true
      })
  }),

  on(OrdersAPiActions.getOrders, (state) => ({...state, isLoading: true})),

  on(OrdersAPiActions.getOrdersSuccess, (state, {orders}) => {
    return adapter.setMany(orders, {...state, isLoading: false, initialized: true})
  }),

  on(OrdersActions.setOrders, (state, {orders, isAppendFirst}) => {
    if (isAppendFirst) {
      const currentOrdersInStore = Object.values(state.entities);
      const ordersArr = uniqBy([...orders, ...currentOrdersInStore], 'id');
      return adapter.setAll(ordersArr, state);
    } else {
      return adapter.setMany(orders, state)
    }
  }),

  on(OrdersActions.setInfiniteScrollOrdersIds, (state, {orderIds, isAppendFirst}) => {
    let newState = state;
    const currentInfiniteScrollOrderIdsInStore = state.infiniteScrollOrderIds;

    if (isAppendFirst) {
      newState = {
        ...newState,
        infiniteScrollOrderIds: uniq([...orderIds, ...currentInfiniteScrollOrderIdsInStore])
      }
    } else {
      newState = {
        ...newState,
        infiniteScrollOrderIds: uniq([...currentInfiniteScrollOrderIdsInStore, ...orderIds])
      }
    }
    return newState;

  }),

  on(OrdersActions.removeOrders, (state, {ids}) => adapter.removeMany(ids, state)),

  on(OrdersActions.removeAll, (state) => adapter.removeAll(state)),

  on(OrdersActions.clearStore, (state) => ({...initialState, initialized: false}))
);

export const ordersFeature = createFeature({
  name: 'Orders',
  reducer,
  extraSelectors: ({
                     selectOrdersState,
                     selectEntities,
                     selectCursor,
                     selectIsLoading,
                     selectInitialized,
                     selectInfiniteScrollOrderIds,
                   }) => ({
    ...adapter.getSelectors(selectOrdersState),
    selectOrders: () => createSelector(
      selectEntities,
      (entities) => Object.values(entities),
    ),
    selectInfiniteScrollOrders: () => createSelector(
      selectEntities,
      selectInfiniteScrollOrderIds,
      (entities, infiniteScrollOrderIds) => {
        const infiniteScrollOrders = [];
        infiniteScrollOrderIds.forEach(id => {
          if (!!entities[id]) {
            infiniteScrollOrders.push(entities[id]);
          }
        })
        return infiniteScrollOrders;
      },
    ),
    selectOrdersById: (ids: string[]) => createSelector(
      selectEntities,
      (entities) => {
        const orders = [];
        ids.forEach(id => {
          if (!!entities[id]) {
            orders.push(entities[id]);
          }
        })
        return orders;
      },
    ),
    selectOrder: (id: string) => createSelector(
      selectEntities,
      (entities: Dictionary<OrderModel>) => entities[id],
    ),
    selectCursor: () => createSelector(
      selectCursor,
      (cursor) => cursor
    ),
    selectIsLoading: () => createSelector(
      selectIsLoading,
      (isLoading) => isLoading
    ),
    selectInitialized: () => createSelector(
      selectInitialized,
      (initialized) => initialized
    )
  })
});
